import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { BarChart3, Target, Zap, CheckCircle, TrendingUp, Clock, Shield, ArrowLeft } from "lucide-react";

export default function MetricsWizardLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-blue-100 p-6">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg">
        {/* Clean Header with TailorWiz Branding */}
        <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg">
          <div className="container mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/')}
                  className="h-9 w-9 p-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="space-y-1">
                  <h1 className="text-2xl font-bold text-foreground">Metrics Wizard</h1>
                  <p className="text-sm text-muted-foreground">Turn experience into data-driven resume bullets</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full border border-primary/20 shadow-sm">
                <span className="text-xs font-medium text-muted-foreground">powered by</span>
                <span className="text-sm font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">TailorWiz</span>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto py-8 px-6 max-w-5xl">
          <div className="w-full">
            {/* Main Content Area */}
            <div className="flex-1 max-w-full">
              {/* Header Section */}
              <div className="mb-12 text-center">
                <div className="space-y-2 mb-8">
                  <h1 className="text-6xl font-extrabold leading-tight tracking-tight">
                    <span className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-white dark:via-slate-100 dark:to-white bg-clip-text text-transparent">
                      Metrics{" "}
                    </span>
                    <span className="bg-gradient-to-r from-primary via-accent to-primary-hover bg-clip-text text-transparent">
                      Wizard
                    </span>
                  </h1>
                  
                  <div className="max-w-4xl mx-auto px-4 mt-4">
                    <p className="text-lg font-semibold bg-gradient-to-r from-primary/80 to-accent/80 bg-clip-text text-transparent leading-relaxed drop-shadow-lg">
                      Turn Your Experience Into Irresistible, Data-Driven Resume Bullets—In Minutes.
                    </p>
                  </div>
                </div>
              </div>

              {/* Problem Statement */}
              <Card className="mb-12 bg-gradient-to-br from-orange-500/5 to-orange-500/10 border-orange-500/20">
          <CardContent className="p-8 text-center">
            <p className="text-lg md:text-xl text-muted-foreground mb-4">
              Most resumes say a lot…
            </p>
            <p className="text-2xl md:text-3xl font-bold text-foreground">
              But very few prove anything.
            </p>
            <p className="text-lg text-muted-foreground mt-4">
              That's where the Metrics Wizard comes in.
            </p>
              </CardContent>
              </Card>

              {/* Value Proposition */}
              <div className="mb-16">
                <p className="text-lg leading-relaxed text-center max-w-4xl mx-auto mb-8">
                  You already have the experience. What you're missing are the numbers that tell the story—the exact figures hiring managers care about. The Metrics Wizard is your personal resume quantification assistant, built to walk you step-by-step through the process of uncovering and inserting the powerful, specific metrics that make hiring teams say "We need to interview this person."
                </p>
              </div>

              {/* How It Works */}
              <div className="mb-16">
                <div className="text-center mb-12">
                  <div className="text-3xl mb-4">🎯</div>
                  <h2 className="text-3xl font-bold text-foreground mb-4">How It Works</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4 mx-auto">
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3">Choose Your Job Experience</h3>
                      <p className="text-muted-foreground">
                        Select the position you want to optimize—whether it's in sales, tech, project management, or customer service.
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-accent/5 to-accent/10 border-accent/20">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-4 mx-auto">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3">Answer Targeted, Role-Specific Questions</h3>
                      <div className="text-muted-foreground">
                        <p className="mb-3">The Metrics Wizard generates highly customized questions based on your role. No fluff, no filler—just the numbers that matter:</p>
                        <ul className="text-sm space-y-1 text-left">
                          <li>• How much revenue did you generate?</li>
                          <li>• How many clients did you manage?</li>
                          <li>• How many deals did you close?</li>
                          <li>• What was the dollar value of your pipeline?</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-orange-500/5 to-orange-500/10 border-orange-500/20">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mb-4 mx-auto">
                        <TrendingUp className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3">See Your Resume Rewritten Instantly</h3>
                      <p className="text-muted-foreground">
                        Once you answer the questions, the Wizard transforms your input into powerful, action-based bullet points—each loaded with quantification and tailored to hiring manager expectations.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Why It's Powerful */}
              <div className="mb-16">
          <div className="text-center mb-12">
            <div className="text-3xl mb-4">💡</div>
            <h2 className="text-3xl font-bold text-foreground mb-4">Why It's So Powerful</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-gradient-to-br from-success/5 to-success/10 border-success/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="text-2xl">📈</div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Resumes with metrics get noticed.</h3>
                    <p className="text-muted-foreground text-sm">
                      Job descriptions list qualifications. Resumes win jobs when they show results. Metrics prove you can perform, not just participate.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-linkedin/5 to-linkedin/10 border-linkedin/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="text-2xl">⚙️</div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Automated + Personalized = Magic</h3>
                    <p className="text-muted-foreground text-sm">
                      We combine automation with expert-crafted, role-specific logic to extract only the numbers that truly matter to recruiters in your field.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="text-2xl">🎯</div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Optimized for Applicant Tracking Systems (ATS)</h3>
                    <p className="text-muted-foreground text-sm">
                      Quantified, metric-driven bullets are more likely to match keyword requirements and pass ATS scans—putting your resume at the top of the pile.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-500/5 to-orange-500/10 border-orange-500/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="text-2xl">⏱️</div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">No Guesswork. No Wasted Time.</h3>
                    <p className="text-muted-foreground text-sm">
                      You'll never stare at a blank screen again. Just answer the questions, and let the Wizard do the heavy lifting.
                    </p>
                  </div>
                </div>
                </CardContent>
                </Card>
              </div>
              </div>

              {/* Target Audience */}
              <Card className="mb-16 bg-gradient-to-br from-accent/5 to-accent/10 border-accent/20">
          <CardContent className="p-8 text-center">
            <div className="text-3xl mb-4">🧠</div>
            <h2 className="text-2xl font-bold text-foreground mb-4">Created for People Who Know Their Worth</h2>
            <h3 className="text-xl text-muted-foreground mb-6">But Need Help Proving It on Paper.</h3>
            <p className="text-lg leading-relaxed max-w-3xl mx-auto">
              Whether you're in sales, project management, healthcare, tech, or education—the Metrics Wizard is your shortcut to a results-focused, interview-ready resume.
            </p>
              </CardContent>
              </Card>

              {/* Final CTA Section */}
              <div className="text-center mb-12">
          <p className="text-lg mb-2">You already did the hard work.</p>
          <p className="text-lg mb-8">Now it's time to show the numbers and get the results.</p>
          
          <div className="text-4xl mb-6">🔥</div>
          <h2 className="text-2xl font-bold mb-4">Try the Metrics Wizard now and see how fast your resume can go from good…</h2>
          <h3 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent mb-8">to undeniable.</h3>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <div className="flex items-center gap-2 text-success">
              <CheckCircle className="w-5 h-5" />
              <span>No writing needed</span>
            </div>
            <div className="flex items-center gap-2 text-success">
              <CheckCircle className="w-5 h-5" />
              <span>No resume jargon</span>
            </div>
            <div className="flex items-center gap-2 text-success">
              <CheckCircle className="w-5 h-5" />
              <span>Just your real experience—turned into results</span>
            </div>
          </div>

          <Button 
            size="lg" 
            className="text-xl px-12 py-6 h-16 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            onClick={() => navigate('/resume-metrics')}
          >
                Start the Metrics Wizard Now
              </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}